import React from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { Close, ExpandMore } from '@mui/icons-material';
import { motion, AnimatePresence } from 'motion/react';
import { ExpandedPanelProps } from '../../../../types/steps';

// Height of the bottom bar (stepper + price + create button)
const BOTTOM_BAR_HEIGHT = 140;

interface ExpandedPanelContainerProps extends ExpandedPanelProps {
  children: React.ReactNode;
  title?: string;
  maxHeight?: string;
}

const ExpandedPanel: React.FC<ExpandedPanelContainerProps> = ({
  children,
  isActive,
  onMinimize,
  onClose,
  title,
  maxHeight = '50vh'
}) => {
  const theme = useTheme();

  return (
    <AnimatePresence>
      {isActive && (
        <>
          {/* Backdrop - positioned above bottom bar */}
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: BOTTOM_BAR_HEIGHT,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1000,
              backdropFilter: 'blur(2px)'
            }}
            onClick={onMinimize}
          />

          {/* Panel - positioned above bottom bar */}
          <Box
            component={motion.div}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 30,
              duration: 0.4
            }}
            sx={{
              position: 'fixed',
              bottom: BOTTOM_BAR_HEIGHT,
              left: 0,
              right: 0,
              maxHeight,
              backgroundColor: theme.palette.background.paper,
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              boxShadow: theme.shadows[16],
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px 12px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                flexShrink: 0
              }}
            >
              {/* Minimize Button */}
              <IconButton
                onClick={onMinimize}
                size="small"
                aria-label="Minimize panel"
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                  }
                }}
              >
                <ExpandMore fontSize="small" />
              </IconButton>

              {/* Title */}
              {title && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '18px',
                    color: theme.palette.text.primary
                  }}
                >
                  {title}
                </Typography>
              )}

              {/* Close Button */}
              <IconButton
                onClick={onClose}
                size="small"
                aria-label="Close panel"
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                  }
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {/* Content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                padding: '20px',
                paddingBottom: '32px'
              }}
            >
              {children}
            </Box>
          </Box>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExpandedPanel;
